import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";
import {
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  IconButton,
  Input,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AddIcon from "@mui/icons-material/Add";
import ReplyIcon from "@mui/icons-material/Reply";
import ModeEdit from "@mui/icons-material/ModeEdit";
import stc from "string-to-color";
import { styled } from "@mui/material/styles";

import {
  store,
  ADD_ORDER,
  SET_QUICK_CREATE_TARGET,
  SET_REPLY_COMMENT_TO,
  SET_EDIT_COMMENT,
} from "./store";

import { SizeContext, DEFAULT_HEIGHT } from "./SizeContext";
import BlacklistButton from "./BlacklistButton";
import RatingButton from "./RatingButton";
import CustomerOwe from "./CustomerOwe";
import { withBusinessOnlyElseHide } from "../../helpers/subscriptionPlan";
import { CACHE_CONTACTS } from "../Constant";
import { AudienceReaction } from "./AudienceReaction";

const CustomerOweTiered = withBusinessOnlyElseHide(CustomerOwe);

// Styled components
const QuantityInput = styled(Input)({
  width: 22,
});

const TextWrapTypography = styled(Typography)({
  width: "80%",
  overflowWrap: "anywhere",
});

const NameText = styled("strong")(({ color }) => ({
  color: stc(color),
}));

const Comment = (props) => {
  const { cache } = useSWRConfig();
  const { dispatch, state } = useContext(store);
  const { unitPrice, itemDesc, specDesc, page, enableCustomerInfo } = state;
  const { comment, index } = props;

  const { data: contact } = useSWRImmutable(
    `${CACHE_CONTACTS}/${comment.from.id}`,
    (key) => cache.get(key)
  );

  const { setSize, listRef } = useContext(SizeContext);

  const root = useRef();

  const {
    id: commentId,
    from,
    message,
    parent,
    can_comment: canComment,
  } = comment;

  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const size = root.current.getBoundingClientRect().height;
    setSize(index, size);
    if (size > DEFAULT_HEIGHT) {
      listRef.current.resetAfterIndex(index, true);
    }
  }, [index, listRef, setSize]);

  const onChangeQuantity = useCallback((e) => {
    setQuantity(e.target.value.replace(/\D/, ""));
  }, []);

  const onKeyDown = useCallback(
    (e) => (e.keyCode === 69 || e.keyCode === 190) && e.preventDefault(),
    []
  );

  const onAdd = useCallback(() => {
    const payload = {
      quantity,
      description: `${itemDesc} ${specDesc}`,
      psid: from.id,
      customerName: from.name,
      commentId,
    };
    dispatch({ type: ADD_ORDER, payload });
  }, [dispatch, from, commentId, quantity, itemDesc, specDesc]);

  const onReplyCommentTo = useCallback(() => {
    const payload = {
      customerName: from.name,
      commentId,
    };
    dispatch({ type: SET_REPLY_COMMENT_TO, payload });
  }, [dispatch, from, commentId]);

  const isPageComment = from.id === page.id;

  return (
    <ListItem ContainerComponent="div" divider ref={root}>
      <ListItemText
        primary={
          <>
            <NameText color={from.id}>{from.name}</NameText>
            {from?.id && <AudienceReaction psid={from.id} pageId={page.id} />}
            {enableCustomerInfo && contact?.psid && (
              <>
                <BlacklistButton contact={contact} />
                <RatingButton record={contact} />
                <CustomerOweTiered contact={contact} />
              </>
            )}
          </>
        }
        secondary={
          <TextWrapTypography variant="body1" color="textPrimary">
            {parent?.from?.name ? `@${parent.from.name} ${message}` : message}
          </TextWrapTypography>
        }
      />
      {!isPageComment && (
        <ListItemSecondaryAction>
          <QuantityInput
            type="number"
            margin="dense"
            value={quantity}
            onChange={onChangeQuantity}
            onKeyDown={onKeyDown}
          />
          <IconButton
            onClick={onAdd}
            aria-label="add n"
            edge="end"
            size="small"
            disabled={!itemDesc}
          >
            <AddIcon />
          </IconButton>
          <IconButton
            edge="end"
            onClick={() => {
              dispatch({ type: SET_QUICK_CREATE_TARGET, payload: comment });
            }}
          >
            <AddBoxIcon />
          </IconButton>
          <IconButton
            disabled={!canComment}
            onClick={onReplyCommentTo}
            aria-label="reply comment to"
            edge="end"
            size="small"
          >
            <ReplyIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              dispatch({ type: SET_EDIT_COMMENT, payload: comment });
            }}
            aria-label="edit comment"
            edge="end"
          >
            <ModeEdit />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

export default Comment;
